/**
 * メモに引用したハイライトの取得キャッシュ。同じ tick に要求された id はまとめて GET /highlights?ids= で取得する
 */

import { useEffect } from 'react'
import { create } from 'zustand'

import { HIGHLIGHT_JUMP_PARAM, type HighlightSource, listHighlightSources } from '@/api/services/highlight'
import { PATHS } from '@/routes/paths'

export type HighlightRefEntry = HighlightSource | 'missing' | 'error'

const useHighlightRefCache = create<{ entries: Map<number, HighlightRefEntry> }>(() => ({ entries: new Map() }))

const setEntries = (pairs: [number, HighlightRefEntry][]) => {
  useHighlightRefCache.setState((state) => {
    const entries = new Map(state.entries)
    for (const [id, entry] of pairs) entries.set(id, entry)
    return { entries }
  })
}

const FETCH_CHUNK_SIZE = 200
const queued = new Set<number>()
const inFlight = new Set<number>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

const fetchChunk = async (ids: number[]) => {
  for (const id of ids) inFlight.add(id)
  try {
    const list = await listHighlightSources({ ids: ids.join(','), limit: ids.length })
    const found = new Map(list.map((h) => [h.id, h]))
    setEntries(ids.map((id) => [id, found.get(id) ?? 'missing']))
  } catch (err) {
    console.warn('[highlight-ref] failed to fetch highlights', err)
    setEntries(ids.map((id) => [id, 'error']))
  } finally {
    for (const id of ids) inFlight.delete(id)
  }
}

const flush = () => {
  flushTimer = null
  const ids = [...queued]
  queued.clear()
  for (let i = 0; i < ids.length; i += FETCH_CHUNK_SIZE) {
    void fetchChunk(ids.slice(i, i + FETCH_CHUNK_SIZE))
  }
}

export const requestHighlightRef = (id: number) => {
  if (queued.has(id) || inFlight.has(id)) return
  queued.add(id)
  if (flushTimer === null) flushTimer = setTimeout(flush, 0)
}

export const putHighlightRefs = (list: HighlightSource[]) => setEntries(list.map((h) => [h.id, h]))

/** highlightId のハイライトを返す。未取得なら undefined */
export const useHighlightRef = (highlightId: number | null): HighlightRefEntry | undefined => {
  const entry = useHighlightRefCache((s) => (highlightId === null ? undefined : s.entries.get(highlightId)))
  useEffect(() => {
    if (highlightId !== null) requestHighlightRef(highlightId)
  }, [highlightId])
  return entry
}

/** 本の中での位置（ページ・進行率・再生位置） */
export const formatHighlightLocation = (h: HighlightSource): string | null => {
  const pos = h.position_data
  const pdfPage = pos.highlightAreas?.[0]?.pageIndex
  if (pdfPage != null) return `p.${pdfPage + 1}`
  const imagePage = pos.selections?.[0]?.page
  if (imagePage != null) return `p.${imagePage + 1}`
  const locations = pos.locator?.locations as { totalProgression?: number } | undefined
  if (locations?.totalProgression != null) return `${Math.round(locations.totalProgression * 100)}%`
  if (pos.timestamp != null) {
    const sec = Math.floor(pos.timestamp)
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`
  }
  return null
}

/** 作品名と（作品名と違えば）話の名前 */
export const formatHighlightSource = (h: HighlightSource): string => {
  const episode = h.episode_title?.trim()
  return episode && episode !== h.product_title ? `${h.product_title} · ${episode}` : h.product_title
}

/** リーダーでハイライトの位置を開く URL */
export const highlightReaderPath = (h: Pick<HighlightSource, 'id' | 'episode_id'>): string =>
  `${PATHS.P.EPISODE.replace(':episode_id', String(h.episode_id))}?${HIGHLIGHT_JUMP_PARAM}=${h.id}`

/** highlightRef ノードの属性（本文と出典はスナップショット） */
export const highlightRefAttrs = (h: HighlightSource) => ({
  highlightId: h.id,
  text: h.text ?? '',
  source: formatHighlightSource(h),
})
